/**
 * The 1-9 build hotbar.
 *
 * Slots are derived from `BUILDINGS` in `kngdm-shared`, not hand-written, so
 * adding the MVP's second building is a data change in `balance.ts` and nothing
 * else — the same derivation `inputController.ts` uses for its number keys, so
 * slot 3 on screen is always slot 3 on the keyboard. Costs and terrain
 * requirements are read from the same table for the same reason: a hardcoded
 * cost here is a lie the moment anyone tunes the real one.
 *
 * Affordability is previewed with `canAfford`/`missingResources` — the shared
 * functions the server uses to authorise the placement. The client is not
 * deciding anything; it is greying out what would be rejected, and colouring
 * exactly which resource line is short so "why can't I build this" is answered
 * on the button rather than in a rejection the player never sees.
 *
 * Deliberately does **not** bind the 1-9 keys. `inputController.ts` owns the
 * keyboard half of the input map; if this panel listened too, both would toggle
 * on one keypress and cancel each other out. The hotbar reflects build mode via
 * `setActive`, whichever route changed it.
 */

import type {
  BuildingDefinition,
  BuildingType,
  ResourceBundle,
  ResourceType,
} from "kngdm-shared";
import {
  BUILDINGS,
  RESOURCE_TYPES,
  buildableBiomes,
  canAfford,
  missingResources,
} from "kngdm-shared";

export interface BuildHotbarCallbacks {
  /** `null` when the active slot is clicked again — the button is its own toggle. */
  onSelect(building: BuildingType | null): void;
}

/** Nine slots per the GDD input map, most of them empty for the MVP. */
const SLOT_COUNT = 9;

/** Declaration order of `BUILDINGS`, matching the controller's hotkey mapping. */
const HOTBAR: readonly BuildingType[] = Object.keys(BUILDINGS) as BuildingType[];

/** Full names for the tooltip, where there is room for them. */
const RESOURCE_LABELS: Record<ResourceType, string> = {
  food: "Food",
  timber: "Timber",
  ore: "Ore",
  gold: "Gold",
  aether: "Aether",
};

const RESOURCE_INITIALS: Record<ResourceType, string> = {
  food: "F",
  timber: "T",
  ore: "O",
  aether: "Æ",
  gold: "G",
};

interface Slot {
  readonly building: BuildingType;
  readonly button: HTMLButtonElement;
  readonly costChips: ReadonlyMap<ResourceType, HTMLElement>;
  /** Held so `destroy` can unregister the exact function it registered. */
  readonly onClick: () => void;
}

export class BuildHotbar {
  readonly element: HTMLElement;

  private readonly callbacks: BuildHotbarCallbacks;
  private readonly slots: Slot[] = [];
  private active: BuildingType | null = null;

  constructor(callbacks: BuildHotbarCallbacks) {
    this.callbacks = callbacks;

    this.element = el("section", "panel panel--hotbar");
    const list = el("ol", "hb");

    for (let index = 0; index < SLOT_COUNT; index++) {
      const building = HOTBAR[index];
      const item = el("li", "hb__item");
      item.append(
        building === undefined
          ? emptySlot(index + 1)
          : this.buildSlot(building, index + 1),
      );
      list.append(item);
    }

    this.element.append(list);
  }

  /** Re-evaluates affordability. Cheap enough to call every tick. */
  update(resources: ResourceBundle): void {
    for (const slot of this.slots) {
      const cost = BUILDINGS[slot.building].cost;
      const affordable = canAfford(resources, cost);
      slot.button.classList.toggle("is-unaffordable", !affordable);

      const missing = new Set<ResourceType>(missingResources(resources, cost));
      for (const [type, chip] of slot.costChips) {
        chip.classList.toggle("is-missing", missing.has(type));
      }
    }
  }

  setActive(building: BuildingType | null): void {
    if (building === this.active) return;
    this.active = building;
    for (const slot of this.slots) {
      slot.button.classList.toggle("is-active", slot.building === building);
      slot.button.setAttribute("aria-pressed", slot.building === building ? "true" : "false");
    }
  }

  destroy(): void {
    for (const slot of this.slots) slot.button.removeEventListener("click", slot.onClick);
    this.slots.length = 0;
    this.element.remove();
  }

  /** Clicking the armed slot disarms it, so the button is its own escape hatch. */
  private select(building: BuildingType, button: HTMLButtonElement): void {
    // Focus would otherwise stay on the button, where a later space keypress —
    // the camera's pan modifier — would re-activate it.
    button.blur();

    const next = building === this.active ? null : building;
    this.setActive(next);
    this.callbacks.onSelect(next);
  }

  /**
   * A slot is an icon and a key number; everything else lives in the tooltip.
   *
   * With six buildings the old layout — label, terrain and a row of cost chips in
   * every slot — was a wall of text along the bottom of the screen competing with
   * the map for attention. An icon row reads at a glance and the detail is one
   * hover away, which is the right trade for information you consult occasionally
   * and a bar you look past constantly.
   */
  private buildSlot(building: BuildingType, slotNumber: number): HTMLElement {
    const definition = BUILDINGS[building];
    const terrain = buildableBiomes(building).join(", ");

    const button = el("button", "hb__slot");
    button.type = "button";
    button.setAttribute("aria-pressed", "false");
    // The accessible name, since the visible content is now an icon and a digit.
    button.setAttribute(
      "aria-label",
      `${definition.label}. ${terrain} only. Key ${slotNumber}.`,
    );

    button.append(iconFor(building), el("span", "hb__key", `${slotNumber}`));

    const { tip, costChips } = buildTooltip(definition, terrain, slotNumber);

    // The wrapper owns the hover, so the tooltip can sit outside the button
    // without the pointer crossing a gap and dismissing it.
    const wrapper = el("span", "hb__slotwrap");
    wrapper.append(button, tip);

    const onClick = (): void => this.select(building, button);
    button.addEventListener("click", onClick);
    this.slots.push({ building, button, costChips, onClick });
    return wrapper;
  }
}


// ---------------------------------------------------------------------------
// Icons
//
// Inline SVG paths rather than an icon font or image files: they inherit
// `currentColor`, so affordability and the armed state are expressed by CSS on
// the button with no second set of assets, and they add nothing to the network.
// Placeholder art by intent — docs/05 says not to spend art budget until the
// gameplay is proven.
// ---------------------------------------------------------------------------

/**
 * One path per building, drawn in a 24x24 box.
 *
 * Keyed by `BuildingType` so the compiler demands an icon for every building.
 * A seventh building added to `balance.ts` will fail to compile here until it
 * has one, which is the right kind of nag — a hotbar of identical blank squares
 * is worse than a build error.
 */
const ICON_PATHS: Record<BuildingType, string> = {
  // Circular saw, matching the map glyph.
  sawmill:
    "M12 3.2a8.8 8.8 0 100 17.6 8.8 8.8 0 000-17.6zm0 3.4a5.4 5.4 0 110 10.8 5.4 5.4 0 010-10.8z M12 1.6l1.7 2.2h-3.4zM12 22.4l-1.7-2.2h3.4zM1.6 12l2.2-1.7v3.4zM22.4 12l-2.2 1.7v-3.4z",
  // Pick over a cut block.
  quarry: "M3 20h18v2H3zM4.6 16.4l7-7 1.6 1.6-7 7zM13.2 4.5c3 .2 5.6 2 6.8 4.7-2.4-.9-4.2-.7-6.2.6l-2.4-2.4c1-1.4 1.6-2.3 1.8-2.9z",
  // Furrows under a sun.
  farm: "M12 2.6a3.2 3.2 0 110 6.4 3.2 3.2 0 010-6.4zM2.5 13h19v2.2h-19zM2.5 17h19v2.2h-19zM2.5 21h19v1.6h-19z",
  // Scales.
  tradingPost:
    "M11 3h2v16h-2zM6 21h12v2H6zM12 5l-6 2 6 2 6-2zM4 9l3 6H1zM20 9l3 6h-6z",
  // Crenellated wall.
  walls:
    "M2 9h3V6h3v3h3V6h3v3h3V6h3v3h1v12H1V9zm4 4v6h3v-6zm7 0v6h3v-6z",
  // Barrel-roofed store with a door.
  storehouse: "M12 2.4L22 8v2H2V8zM4 12h16v10h-6v-6h-4v6H4z",
};

/**
 * Human-readable effects, derived from the definition rather than written out.
 *
 * Every building's effects therefore describe themselves, and adding a seventh —
 * or retuning a multiplier — needs no edit here. Hardcoding "Timber x2.2" would
 * be a lie the moment anyone touches `balance.ts`, which is exactly the failure
 * this panel already avoids for costs and terrain.
 */
function describeEffects(definition: BuildingDefinition): string[] {
  const effects: string[] = [];

  for (const type of RESOURCE_TYPES) {
    const multiplier = definition.yieldMultiplier[type];
    if (multiplier === undefined || multiplier === 1) continue;
    effects.push(`${RESOURCE_LABELS[type]} x${multiplier}`);
  }
  if (definition.defenceMultiplier !== 1) {
    effects.push(`Defence x${definition.defenceMultiplier}`);
  }
  if (definition.storageBonus !== 0) {
    effects.push(`+${definition.storageBonus} storage`);
  }

  // Never empty: a tooltip with a blank effects line reads as a bug rather than
  // as a building that happens to do nothing.
  return effects.length > 0 ? effects : ["No direct effect"];
}

function iconFor(building: BuildingType): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("hb__icon");

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", ICON_PATHS[building]);
  path.setAttribute("fill", "currentColor");
  svg.append(path);
  return svg;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * The tooltip: name, terrain gate, effects and cost.
 *
 * Built once per slot and shown with CSS rather than created on hover, because
 * `update` runs at the simulation's 10Hz and marks unaffordable cost chips — the
 * chips have to exist for that to have anywhere to write.
 */
function buildTooltip(
  definition: BuildingDefinition,
  terrain: string,
  slotNumber: number,
): { tip: HTMLElement; costChips: Map<ResourceType, HTMLElement> } {
  const tip = el("span", "hb__tip");
  tip.setAttribute("role", "tooltip");

  tip.append(el("span", "hb__tipname", definition.label));
  tip.append(el("span", "hb__tipterrain", terrain));

  const effects = el("span", "hb__tipeffects");
  for (const effect of describeEffects(definition)) {
    effects.append(el("span", "hb__tipeffect", effect));
  }
  tip.append(effects);

  const costRow = el("span", "hb__cost");
  const costChips = new Map<ResourceType, HTMLElement>();
  for (const type of RESOURCE_TYPES) {
    const amount = definition.cost[type];
    if (amount <= 0) continue;
    const chip = el("span", "hb__chip", `${amount}${RESOURCE_INITIALS[type]}`);
    costChips.set(type, chip);
    costRow.append(chip);
  }
  tip.append(costRow);

  tip.append(el("span", "hb__tiphint", `Press ${slotNumber}, then click a hex`));
  return { tip, costChips };
}

function emptySlot(slotNumber: number): HTMLElement {
  const slot = el("span", "hb__slot hb__slot--empty");
  slot.append(el("span", "hb__key", `${slotNumber}`));
  return slot;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
